import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { fetchCurrentUser } from '../../ducks/user.duck';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/FavouriteListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/FavouriteListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/FavouriteListingsPage/FETCH_LISTINGS_ERROR';

export const ADD_ENTITIES = 'app/FavouriteListingsPage/ADD_ENTITIES';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  entities: {},
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  // Copy each entity-type map (e.g. entities.listing) so updatedEntities, which
  // assigns into these maps in place, does not mutate the previous state. This
  // matters when ADD_ENTITIES runs again (e.g. refreshing after un-favouriting)
  // and the entities already exist in the store.
  const entitiesCopy = Object.keys(state.entities).reduce((acc, type) => {
    acc[type] = { ...state.entities[type] };
    return acc;
  }, {});
  return {
    ...state,
    entities: updatedEntities(entitiesCopy, apiResponse),
  };
};

const favouriteListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case ADD_ENTITIES:
      return merge(state, payload);

    default:
      return state;
  }
};

export default favouriteListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getListingsById = (state, listingIds) => {
  const { entities } = state.FavouriteListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'listing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(entities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

export const addEntities = sdkResponse => ({
  type: ADD_ENTITIES,
  payload: sdkResponse,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryListings = queryParams => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));
  const { perPage, ...rest } = queryParams;
  const params = { ...rest, perPage };

  return sdk.listings
    .query(params)
    .then(response => {
      dispatch(addEntities(response));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

// Re-run the favourites query using the current user's (possibly just updated)
// favourites list. Used to refresh the page after a listing is un-favourited so
// the removed listing disappears without a full page reload.
export const reloadListings = config => (dispatch, getState) => {
  const page = getState().FavouriteListingsPage.pagination?.page || 1;
  return dispatch(loadData({}, `?page=${page}`, config));
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return dispatch(fetchCurrentUser())
    .then(response => {
      const currentUser = getState().user.currentUser;
      const ids = currentUser?.attributes?.profile?.privateData?.favourites || [];
      return dispatch(
        queryListings({
          ...queryParams,
          ids: ids,
          page,
          perPage: RESULT_PAGE_SIZE,
          include: ['author', 'author.profileImage', 'images'],
          'fields.image': [
            `variants.${variantPrefix}`,
            `variants.${variantPrefix}-2x`,
            // Avatars
            'variants.square-small',
            'variants.square-small2x',
          ],
          ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
          'limit.images': 1,
        })
      );
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};
